import { UploadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { Flex } from '@/components/base';
import Button from '@/components/Button/Button';
import DataTable from '@/components/DataTable';
import ColumnCustomizer from '@/components/DataTable/ColumnCustomizer';
import Sorter from '@/components/DataTable/Sorter';
import DataFilter from '@/components/Filter/DataFilter';
import SearchInput from '@/components/Inputs/SearchInput';
import StyledPagination from '@/components/Pagination/Pagination';
import { PageHeaderTitle } from '@/components/Typography/NamedTypo';
import Typo from '@/components/Typography/Typo';
import { CUSTOMER_INITIAL_SORT_STATUS } from '@/configs/filter';
import {
  useCustomerHeader,
  useCustomerTableData,
} from '@/data-table-configs/customers';
import PageHeaderStyle from '@/features/Commons/PageLayout/PageHeader';
import CreateCustomerModal from '@/features/Contact/CreateCustomerModal';
import useAppInit from '@/hooks/domain/App/useAppInit';
import useColor from '@/hooks/domain/useColor';
import { useStorageFilterQuery } from '@/hooks/domain/useFilterQuery';
import useRedirect from '@/hooks/domain/useRedirect';
import useUserRole from '@/hooks/domain/useUserRole';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import useStates from '@/hooks/pure/useStates';
import service from '@/services/index';
import useCustomerStore, { CUSTOMER_PAGE_SIZE } from '@/stores/CustomerStore';
import useColumnConfigStore from '@/stores/DataTable/ColumnConfigStore';
import useModalStore from '@/stores/pure/ModalStore';
import { CONTACT_COLUMNS } from '@/types/filterKeyConst';
import filterUtils from '@/utils/filter';
import { filterMap } from '@/utils/lazy';
import { buildQuery, highlight, removeHighlight } from '@/utils/util';

const CustomerPage = () => {
  const wr = useWorkspaceRouter();
  const color = useColor();
  const appInit = useAppInit();
  const { isEditor } = useUserRole();
  const [paginatedData, setPaginatedData] = useState<
    PaginatedData<CustomerT>[]
  >([]);

  useRedirect();

  const {
    createModalStatus,
    listLoading,
    selectedCustomer,
    page,
    setCreateModalStatus,
    fetchCustomerList,
  } = useCustomerStore((state) => ({
    createModalStatus: state.createModalStatus,
    listLoading: state.listLoading,
    page: state.page,
    selectedCustomer: state.selectedCustomer,
    setCreateModalStatus: state.setCreateModalStatus,
    fetchCustomerList: state.fetchCustomerList,
  }));

  const { currFilterSetQuery, onFilterChange, clearFilter } =
    useStorageFilterQuery('customer');

  const [sortStatus, setSortStatus] = useState<NotNullableSortStatusT>(
    CUSTOMER_INITIAL_SORT_STATUS,
  );

  const setModalStatus = useModalStore((state) => state.setModalStatus);

  const { toggleColumnVisible, changeColumnOrder, changeColumnWidth } =
    useColumnConfigStore(
      ({ toggleColumnVisible, changeColumnOrder, changeColumnWidth }) => ({
        toggleColumnVisible,
        changeColumnOrder,
        changeColumnWidth,
      }),
    );

  type States = {
    searchValue: string;
    searchInput: string;
  };

  const { state, setValue, setState } = useStates<States>({
    searchValue: '',
    searchInput: '',
  });

  const clearSearch = () => {
    setState({
      searchInput: '',
      searchValue: '',
    });
  };

  const fetchList = useCallback(
    async (prevData?: PaginatedData<CustomerT>[], page?: number) => {
      const data = await fetchCustomerList({
        page,
        searchValue: state.searchValue,
        prevData: page ? prevData : [],
        fiterSetQuery: currFilterSetQuery,
        sortStatus,
      });
      setPaginatedData(data);
      if (state.searchValue) {
        highlight(state.searchValue);
      } else {
        removeHighlight();
      }
    },
    [currFilterSetQuery, fetchCustomerList, sortStatus, state.searchValue],
  );

  useEffect(() => {
    if (appInit) {
      fetchList();
    }
  }, [appInit, fetchList]);

  const filterQuery = useMemo(() => {
    const query = filterUtils.getFilterStatusQuery(currFilterSetQuery);
    if (state.searchValue.trim() !== '') {
      query.push({
        key: CONTACT_COLUMNS.NAME_SEARCH,
        value: state.searchValue,
      });
    }

    return buildQuery(query);
  }, [currFilterSetQuery, state.searchValue]);

  const { data: totalCount } = useQuery({
    queryKey: ['customer-list-count', filterQuery],
    queryFn: () => service.contact.fetchCustomerCount(filterQuery),
    enabled: isEditor,
  });

  const customerHeader = useCustomerHeader();
  const tableData = useCustomerTableData(paginatedData);

  return (
    <Style d="column">
      <PageHeaderStyle>
        <Flex g={12} a="center">
          <PageHeaderTitle>Contacts</PageHeaderTitle>
          <Typo type="label1" color={color.gray600}>
            {totalCount || 0}
          </Typo>
          <SearchInput
            placeholder="Search"
            value={state.searchInput}
            onChange={(e) => setValue('searchInput', e.currentTarget.value)}
            onBlur={() => setValue('searchValue', state.searchInput)}
            onClickClear={clearSearch}
          />
        </Flex>
        {isEditor && (
          <Flex a="center" g="6px">
            <Button
              leftIcon={<UploadOutlined />}
              sizeType="m"
              variant="secondary"
              onClick={() => {
                wr.push('/customers/upload');
                setModalStatus('customerUploadModal', true);
              }}
            >
              Upload
            </Button>
            <Button
              leftIcon={<Plus />}
              sizeType="m"
              onClick={() => setCreateModalStatus(true)}
            >
              Contact
            </Button>
          </Flex>
        )}
      </PageHeaderStyle>
      <Flex padding="0px 32px">
        <DataFilter
          tableName="customer"
          filterOptions={customerHeader}
          currFilterSetQuery={currFilterSetQuery}
          onFilterChange={onFilterChange}
          resetFilter={clearFilter}
        />
        <Flex>
          <Sorter
            options={filterMap(customerHeader, (col) => {
              if (!col.sortKey || !col.orderable) return null;
              return {
                sortKey: col.sortKey,
                sortName: col.columnName,
                orderType: col.orderType || 'default',
              };
            })}
            currSortStatus={sortStatus}
            onSortChange={setSortStatus}
          />
          <ColumnCustomizer
            columns={customerHeader}
            onCustomizeColumnSelect={(key) => {
              toggleColumnVisible('contact_view', key);
            }}
          />
        </Flex>
      </Flex>
      <DataTable
        style={{
          height: 'auto',
          maxHeight: 'calc(100vh - 140px)',
        }}
        name="customer"
        id="customer-data-table"
        columns={customerHeader}
        datas={tableData}
        sortStatus={sortStatus}
        onSortChange={setSortStatus}
        selectedRowKey={selectedCustomer}
        loading={listLoading}
        asyncData
        customizable
        columnChangeOrderHandler={(key, order) =>
          changeColumnOrder('contact_view', key, order)
        }
        columnResizeHandler={(key, width) =>
          changeColumnWidth('contact_view', key, width)
        }
        onCustomizeColumnSelect={(key) =>
          toggleColumnVisible('contact_view', key)
        }
        openButtonOffsetX={130}
      />
      <PaginationWrapper>
        <StyledPagination
          loading={listLoading}
          pageSize={CUSTOMER_PAGE_SIZE}
          currentLength={tableData.length}
          total={totalCount || 0}
          current={page}
          onChange={(page) => {
            fetchList(paginatedData, page);
          }}
        />
      </PaginationWrapper>
      {createModalStatus && <CreateCustomerModal />}
    </Style>
  );
};

const Style = styled(Flex)`
  width: 100%;
  height: 100vh;
  position: relative;
  .customer-list-filter-sort {
    width: 100%;
    padding: 0 8px;
    z-index: 10;
    background-color: ${(props) => props.theme.colors.gray100};
  }
`;

const PaginationWrapper = styled(Flex).attrs({
  a: 'center',
  j: 'flex-start',
})`
  border-top: 1px solid ${(props) => props.theme.colors.ND04};
  width: 100%;
  height: 44px;
  padding: 0 32px;
`;

export default CustomerPage;
